import type { Uuid } from './common';

// --- Certificates ---

export interface CertificateTemplateSummary {
  id: Uuid;
  name: string;
  certificate_type: string;
  template_json: Record<string, unknown>;
  is_default: boolean;
}

export interface ListCertificateTemplatesResponse {
  templates: CertificateTemplateSummary[];
}

export interface CreateCertificateTemplateRequest {
  name: string;
  certificate_type: string;
  template_json: Record<string, unknown>;
  is_default?: boolean;
}

export interface CreateCertificateTemplateResponse {
  template_id: Uuid;
}

export interface UpdateCertificateTemplateRequest {
  name: string;
  template_json: Record<string, unknown>;
  is_default?: boolean;
}

export interface UpdateCertificateTemplateResponse {
  template: CertificateTemplateSummary;
}

export interface IssueCertificateRequest {
  student_id: string;
  certificate_type: string;
  template_id?: string | null;
  issue_date?: string | null;
  valid_until?: string | null;
  purpose?: string | null;
  remarks?: string | null;
}

export interface IssueCertificateResponse {
  certificate_id: Uuid;
}

export interface PurgeExpiredCertificatesResponse {
  deleted_admit_cards: number;
  deleted_fee_paid: number;
  deleted_media_objects: number;
  s3_objects_deleted: number;
  admit_card_purge_after_years: number;
  fee_paid_purge_after_years: number;
  skipped_permanent_types: number;
}

export interface OutstandingInvoiceSummary {
  invoice_id: Uuid;
  billing_month: string;
  outstanding_cents: number;
  status: string;
}

export interface AdmitCardAudienceRequest {
  term: string;
  academic_year: string;
  fees_clear_through_month: string;
  session_start_month?: string | null;
  exam_id?: Uuid | null;
  exam_name?: string | null;
  exam_date?: string | null;
  class_id?: Uuid | null;
  section_id?: Uuid | null;
  student_ids?: Uuid[] | null;
  /** When set, eligible if total outstanding in the fee window is <= this amount (cents). */
  max_pending_amount_cents?: number | null;
}

export interface AdmitCardStudentPreview {
  student_id: Uuid;
  student_name: string;
  admission_no: string;
  eligible: boolean;
  outstanding_invoices: OutstandingInvoiceSummary[];
}

export interface PreviewAdmitCardEligibilityResponse {
  term: string;
  academic_year: string;
  fees_clear_through_month: string;
  session_start_month: string;
  max_pending_amount_cents?: number | null;
  total_students: number;
  eligible_count: number;
  ineligible_count: number;
  students: AdmitCardStudentPreview[];
  schedule_ready?: boolean;
  schedule_total_slots?: number;
  schedule_complete_slots?: number;
  schedule_missing?: ExamTimetableGap[];
  timetable_rows?: ExamTimetableEntry[];
}

export function normalizeAdmitPreview(
  raw: PreviewAdmitCardEligibilityResponse | null | undefined
): PreviewAdmitCardEligibilityResponse | null {
  if (!raw) return null;
  return {
    ...raw,
    students: (raw.students ?? []).map((student) => ({
      ...student,
      outstanding_invoices: student.outstanding_invoices ?? [],
    })),
    schedule_ready: raw.schedule_ready ?? true,
    schedule_total_slots: raw.schedule_total_slots ?? 0,
    schedule_complete_slots: raw.schedule_complete_slots ?? 0,
    schedule_missing: raw.schedule_missing ?? [],
    timetable_rows: raw.timetable_rows ?? [],
  };
}

export interface ExamTimetableEntry {
  id: string;
  exam_id: string;
  class_id: string;
  class_name: string;
  subject_id: string;
  subject_name: string;
  term: string;
  academic_year: string;
  max_marks: number;
  exam_date: string;
  start_time: string;
  end_time: string;
  room?: string | null;
}

export interface ExamTimetableGap {
  class_name: string;
  subject_name: string;
  reason: string;
}

export interface ListExamTimetableResponse {
  rows: ExamTimetableEntry[];
}

export interface SaveExamTimetableRow {
  class_id: string;
  subject_id: string;
  max_marks: number;
  exam_date: string;
  start_time: string;
  end_time: string;
  room?: string | null;
}

export interface SaveExamTimetableRequest {
  exam_id: string;
  term: string;
  academic_year: string;
  rows: SaveExamTimetableRow[];
}

export interface ExamTimetableCsvRequest {
  exam_id: string;
  term: string;
  academic_year: string;
  csv_text: string;
  class_id?: string | null;
}

export interface ExamTimetableCsvPreviewRow {
  line: number;
  class_name: string;
  subject_name: string;
  max_marks: number;
  exam_date: string;
  start_time: string;
  end_time: string;
  room?: string | null;
  error?: string | null;
}

export interface ExamTimetableCsvPreviewResponse {
  valid_rows: number;
  error_rows: number;
  rows: ExamTimetableCsvPreviewRow[];
  errors: string[];
}

export interface ImportExamTimetableCsvResponse {
  imported: number;
}

export interface GeneratedAdmitCardSummary {
  certificate_id: Uuid;
  student_id: Uuid;
  student_name: string;
  admission_no: string;
  pdf_media_id?: Uuid | null;
}

export interface SkippedAdmitCardSummary {
  student_id: Uuid;
  student_name: string;
  admission_no: string;
  reason: string;
  outstanding_invoices: OutstandingInvoiceSummary[];
}

export interface GenerateAdmitCardsRequest extends AdmitCardAudienceRequest {
  template_id?: Uuid | null;
  issue_date?: string | null;
  skip_ineligible?: boolean | null;
  /** When true, replaces admit cards already generated for this term and academic year. */
  replace_existing?: boolean | null;
}

export interface GenerateAdmitCardsResponse {
  generated: GeneratedAdmitCardSummary[];
  skipped: SkippedAdmitCardSummary[];
}

export interface AdmitCardsBulkJobResult extends GenerateAdmitCardsResponse {
  pdf_uploaded_count?: number;
  pdf_upload_failed_count?: number;
  storage_enabled?: boolean;
  storage_note?: string | null;
}

export interface AdmitCardClassStats {
  class_id: string;
  class_name: string;
  eligible: number;
  ineligible: number;
  generated: number;
  not_generated: number;
  unique_downloads: number;
  total_downloads: number;
  pending_download: number;
}

export interface AdmitCardDashboardStats {
  configured: boolean;
  term: string | null;
  academic_year: string | null;
  fees_clear_through_month: string | null;
  exam_name: string | null;
  total_students: number;
  eligible: number;
  ineligible: number;
  generated: number;
  not_generated: number;
  unique_downloads: number;
  total_downloads: number;
  duplicate_downloads: number;
  pending_download: number;
  by_class: AdmitCardClassStats[];
}

export interface RenderCertificateResponse {
  certificate_id: Uuid;
  template_json: Record<string, unknown>;
  data: Record<string, unknown>;
}
